import React, { useEffect, useMemo, useRef, useState } from "react";
import { ClipboardList, Plus, Check, X, Archive, Pencil, Lightbulb, MessageCircleQuestion, Palmtree, Trash2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import ConfirmDialog from "./ConfirmDialog.jsx";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import RostouciPole from "./RostouciPole.jsx";

// Masterplán klienta (8.7.2026, Vylepšení portálu 4.0).
// 9.7.2026 Masterplan 2.0:
//  - emoji + KPI u úkolu (např. 🚶 + "10000 kroků"), klient je vidí v portálu
//  - žádosti klienta o změnu plánu (vyřizuje CSM/owner tady)
//  - volno od plánu: informace o aktivním/naplánovaném volnu, dny volna
//    v tabulce plnění šedě
//  - automatické denní hodnocení (sloupec hodnoceni v masterplan_dny) se
//    generuje pro portál, tady je vidět v tooltipu dne
//  - onCount hlásí počet aktivních úkolů do hlavičky ExpandableSection
// CSM/owner/manager přiřazuje 3-5 denních úkolů, klient je odškrtává v portálu,
// trenér vidí plnění a může navrhovat úkoly (management schvaluje).
// Data: /api/masterplan (PG masterplan_ukoly / plneni / dny / volno / zadosti).

// /api/clients vrací jen `notion` URL, ne notion_id (mirror ClientDetiSection).
function notionIdFromUrl(u) {
  const m = String(u || "").match(/([0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
  return m ? m[1] : "";
}

function lastNDays(n) {
  const out = [];
  const t = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(t.getFullYear(), t.getMonth(), t.getDate() - i);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    out.push({ iso, label: `${d.getDate()}.${d.getMonth() + 1}.` });
  }
  return out;
}

function czDate(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${parseInt(m[3], 10)}. ${parseInt(m[2], 10)}. ${m[1]}` : iso;
}

// 10.7.2026 — týdenní úkoly: pondělí ISO týdne + posun dne (mirror
// masterplanLib.cjs, aby admin počítal plnění stejně jako portál).
function weekMondayIso(iso) {
  const d = new Date(iso + "T12:00:00Z");
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day);
  return d.toISOString().slice(0, 10);
}
function addDaysIsoLocal(iso, n) {
  const d = new Date(iso + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

const MESICE_CZ = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];

// 9.9.2026 — kalendar jednoho mesice u ukolu masterplanu.
// dnyMesice vraci vsechny dny mesice a kolik prazdnych bunek dat pred prvni den,
// aby prvni sloupec mrizky bylo pondeli.
function dnyMesice(mesic) {
  const [y, m] = String(mesic).split("-").map((v) => parseInt(v, 10));
  const pocet = new Date(y, m, 0).getDate();
  const dny = [];
  for (let i = 1; i <= pocet; i++) {
    dny.push({ iso: `${y}-${String(m).padStart(2, "0")}-${String(i).padStart(2, "0")}`, den: i });
  }
  return { dny, offset: (new Date(y, m - 1, 1).getDay() + 6) % 7 };
}
function mesicLabel(mesic) {
  const [y, m] = String(mesic).split("-");
  const nazev = MESICE_CZ[parseInt(m, 10) - 1] || m;
  return `${nazev.charAt(0).toUpperCase()}${nazev.slice(1)} ${y}`;
}
function posunMesic(mesic, kolik) {
  const [y, m] = String(mesic).split("-").map((v) => parseInt(v, 10));
  const d = new Date(y, m - 1 + kolik, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
const DNY_ZKRATKY = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];
const TRIDA_DNE = {
  splneno: "bg-plum-600 text-white",
  nesplneno: "bg-rose-100 text-rose-700",
  volno: "bg-sky-50 text-sky-500",
  otevreno: "bg-slate-100 text-slate-400",
  mimo: "bg-slate-50 text-slate-300",
};
const POPIS_DNE = {
  splneno: "Splněno",
  nesplneno: "Nesplněno",
  volno: "Volno od plánu",
  otevreno: "Den je ještě otevřený",
  mimo: "Mimo plán (úkol tehdy neexistoval nebo je den v budoucnu)",
};

// Rychlá nabídka emoji pro úkoly (dle typologie starých masterplánů:
// kroky, spánek, strava, kardio, pití, cvičení, procházky).
const EMOJI_PRESETS = ["🚶", "👟", "😴", "🥗", "🍗", "💧", "🏃", "🚴", "💪", "🧘", "📖", "🎯"];

export default function MasterplanSection({ klientId, klientNotion, klientName, readOnly = false, onCount }) {
  const id = klientId || notionIdFromUrl(klientNotion);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState("");
  const [newEmoji, setNewEmoji] = useState("");
  const [newKpi, setNewKpi] = useState("");
  const [newTyp, setNewTyp] = useState("denni"); // denni | tydenni
  const [newCil, setNewCil] = useState(1); // cíl týdenního úkolu: kolikrát/týden (1-7)
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editEmoji, setEditEmoji] = useState("");
  const [editKpi, setEditKpi] = useState("");
  const [editTyp, setEditTyp] = useState("denni");
  const [editCil, setEditCil] = useState(1);
  const [koucNote, setKoucNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false); // "Uloženo ✓" feedback po uložení
  const [err, setErr] = useState("");
  // Designový confirm (ConfirmDialog) místo nativního window.confirm:
  // { title, description, confirmLabel, variant, onConfirm }
  const [confirmState, setConfirmState] = useState(null);
  // Poznámky klienta po měsících: { "2026-07": true/false } (nejnovější měsíc
  // je otevřený defaultně, viz noteMonths níže).
  const [openMonths, setOpenMonths] = useState({});
  // 9.9.2026 — telefon ma jine rozlozeni ukolu, formular na pridani je schovany
  // za tlacitkem a vysvetlivky k tabulce jsou sbalene.
  const uzka = useUzkaObrazovka();
  const [pridavaniOtevreno, setPridavaniOtevreno] = useState(false);
  // 9.9.2026 — rozbaleny ukol s historii, vybrany mesic a cache stazenych mesicu
  // ({ "2026-09": { "2026-09-01": { ukol_id: true } } }).
  const [otevrenyUkol, setOtevrenyUkol] = useState(null);
  const [mesic, setMesic] = useState(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`;
  });
  const [mesicCache, setMesicCache] = useState({});
  const [mesicNacita, setMesicNacita] = useState(false);

  const user = getCurrentUser();
  const role = user?.role || "";
  const canManage = !readOnly && ["owner", "manager", "csm", "nutritionist"].includes(role);
  const canSuggest = !readOnly && role === "trener";
  const isOwner = !readOnly && role === "owner";

  const days = useMemo(() => lastNDays(14), []);
  // Tabulka plneni se ukazuje jen na pocitaci, tam je vzdy vsech 14 dnu.
  // Na telefonu je misto ni historie po mesicich u jednotlivych ukolu.
  const zobrazeneDny = days;
  const todayIso = days[days.length - 1].iso;

  const load = async () => {
    if (!id) { setLoading(false); return; }
    try {
      // days=22: tabulka ukazuje 14 dní, ale týdenní úkoly potřebují i plnění
      // z pondělí týdne prvního zobrazeného dne (až 6 dní před oknem).
      const r = await apiFetch(`/api/masterplan?klient_id=${encodeURIComponent(id)}&days=22`);
      const j = await r.json();
      if (j.ok) {
        setData(j);
        if (onCount) {
          const akt = (j.ukoly || []).filter((u) => u.stav === "aktivni").length;
          const pending = (j.ukoly || []).filter((u) => u.stav === "navrh").length + (j.zadosti || []).length;
          onCount(akt, pending);
        }
      }
    } catch (_) { /* ticho */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]);

  // 9.9.2026 — plneni za vybrany mesic se stahuje az kdyz je nejaky ukol rozbaleny,
  // a kazdy mesic jen jednou (drzi se v mesicCache). Bez rozbaleni se nic navic netaha.
  useEffect(() => {
    if (!id || !otevrenyUkol) return undefined;
    if (mesicCache[mesic] !== undefined) return undefined;
    let zruseno = false;
    setMesicNacita(true);
    (async () => {
      let mapa = {};
      try {
        const r = await apiFetch(`/api/masterplan?klient_id=${encodeURIComponent(id)}&days=1&mesic=${mesic}`);
        const j = await r.json();
        if (j.ok && j.plneniMesic) mapa = j.plneniMesic;
      } catch (_) { /* ticho, ukaze se prazdny mesic */ }
      if (zruseno) return;
      setMesicCache((c) => ({ ...c, [mesic]: mapa }));
      setMesicNacita(false);
    })();
    return () => { zruseno = true; };
  }, [id, otevrenyUkol, mesic]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link z nutri dashboardu (?focus=mp-sekce|mp-zadosti|mp-komentare):
  // po načtení sroluje na část k řešení a na 2,5 s ji zvýrazní.
  const focusDone = useRef(false);
  useEffect(() => {
    if (loading || focusDone.current) return;
    let focus = "";
    try { focus = new URLSearchParams(window.location.search).get("focus") || ""; } catch { /* ignore */ }
    if (!["mp-sekce", "mp-zadosti", "mp-komentare"].includes(focus)) return;
    focusDone.current = true;
    setTimeout(() => {
      const el = document.getElementById(focus) || document.getElementById("mp-sekce");
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("ring-2", "ring-plum-400", "rounded-xl");
      setTimeout(() => el.classList.remove("ring-2", "ring-plum-400"), 2500);
    }, 350);
  }, [loading]);

  const post = async (payload) => {
    setErr("");
    try {
      const r = await apiFetch("/api/masterplan", { method: "POST", body: JSON.stringify(payload) });
      const j = await r.json();
      if (!j.ok) { setErr(j.error || "Uložení selhalo"); return false; }
      return true;
    } catch (e) { setErr(e.message || "Uložení selhalo"); return false; }
  };

  const addTask = async () => {
    const text = newText.trim();
    if (!text) return;
    setAdding(true);
    if (await post({ action: "add", klient_id: id, klient_name: klientName, text, emoji: newEmoji.trim(), kpi: newKpi.trim(), typ: newTyp, cil_tydne: newCil })) {
      setNewText(""); setNewEmoji(""); setNewKpi(""); setNewTyp("denni"); setNewCil(1);
      await load();
    }
    setAdding(false);
  };

  const saveEdit = async () => {
    if (await post({ action: "edit", id: editingId, text: editText.trim(), emoji: editEmoji.trim(), kpi: editKpi.trim(), typ: editTyp, cil_tydne: editCil })) {
      setEditingId(null);
      await load();
    }
  };

  const archive = (ukol) => {
    setConfirmState({
      title: "Archivovat úkol?",
      description: <>Úkol <strong>{ukol.text}</strong> klientovi zmizí z portálu. Historie plnění zůstane zachovaná.</>,
      confirmLabel: "Archivovat",
      variant: "warning",
      onConfirm: async () => { if (await post({ action: "archive", id: ukol.id })) await load(); },
    });
  };

  const approve = async (ukolId) => {
    if (await post({ action: "approve", id: ukolId })) await load();
  };

  // Tvrdé smazání (jen owner): pryč jde i historie plnění, hodí se na omyly
  // a testovací úkoly. Pro běžné schování slouží archivace.
  const hardDelete = (ukol) => {
    setConfirmState({
      title: "Smazat úkol natrvalo?",
      description: <>Úkol <strong>{ukol.text}</strong> se smaže včetně celé historie plnění. Tohle nejde vrátit. Pro běžné schování použijte archivaci.</>,
      confirmLabel: "Smazat natrvalo",
      variant: "danger",
      onConfirm: async () => { if (await post({ action: "delete", id: ukol.id })) await load(); },
    });
  };

  const resolveZadost = async (zadostId) => {
    if (await post({ action: "zadost-vyrizena", id: zadostId })) await load();
  };

  // Smazání poznámky kouče (celý den) — s designovým potvrzením.
  const deleteKoucNote = (d) => {
    setConfirmState({
      title: "Smazat poznámku kouče?",
      description: <>Poznámka z <strong>{czDate(d.datum)}</strong> se smaže i klientce z portálu.</>,
      confirmLabel: "Smazat",
      variant: "danger",
      onConfirm: async () => { if (await post({ action: "kouc-note-delete", klient_id: id, datum: d.datum })) await load(); },
    });
  };

  const saveKoucNote = async () => {
    const text = koucNote.trim();
    if (!text) return;
    setSavingNote(true);
    setNoteSaved(false);
    // Přidává další poznámku (komentář), historie zůstává — nic se nepřepisuje.
    if (await post({ action: "kouc-note", klient_id: id, datum: todayIso, poznamka: text })) {
      setKoucNote("");
      await load();
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 3000);
    }
    setSavingNote(false);
  };

  if (!id) return null;
  if (loading) return <div className="text-sm text-slate-400 py-2">Načítám masterplán…</div>;

  const ukoly = data?.ukoly || [];
  const aktivni = ukoly.filter((u) => u.stav === "aktivni");
  const navrhy = ukoly.filter((u) => u.stav === "navrh");
  // Masterplán jen pro balíčky Koučink FLEXIBLE / GOLD / PREMIUM (9.7.2026).
  const eligible = data?.eligible !== false;
  const plneni = data?.plneni || {};
  // Poznámky klienta: plná historie (poznamkyHistorie, bez 14denního okna);
  // fallback na dny pro starší odpověď API.
  const dnyKlient = data?.poznamkyHistorie || (data?.dny || []).filter((d) => d.poznamka_klient);
  const koucNotesAll = data?.koucHistorie || (data?.dny || []).filter((d) => d.poznamka_kouc);
  // Seskupení poznámek po měsících, nejnovější první (pro rozbalovací sekce).
  const groupByMonth = (rows) => {
    const sorted = [...rows].sort((a, b) => (a.datum < b.datum ? 1 : -1));
    const byKey = {};
    const groups = [];
    for (const d of sorted) {
      const key = String(d.datum).slice(0, 7); // YYYY-MM
      if (!byKey[key]) {
        const [y, m] = key.split("-");
        byKey[key] = { key, label: `${MESICE_CZ[parseInt(m, 10) - 1] || key} ${y}`, notes: [] };
        groups.push(byKey[key]);
      }
      byKey[key].notes.push(d);
    }
    return groups;
  };
  const noteMonths = groupByMonth(dnyKlient);
  const koucMonths = groupByMonth(koucNotesAll);
  const zadosti = data?.zadosti || [];
  const volno = data?.volno || {};
  const volnoDny = data?.volnoDny || [];

  const isVolnoDay = (iso) => volnoDny.some((v) => {
    const od = String(v.od).substring(0, 10), doD = String(v.do || v.do_dne).substring(0, 10);
    return od <= iso && iso <= doD;
  });

  // Týdenní úkol je splněný pro KAŽDÝ den svého ISO týdne (Po-Ne), jakmile
  // ho klientka odškrtne v aspoň cil_tydne dnech toho týdne (default 1 —
  // původní chování). Mirror masterplanLib.isUkolDoneForDay. 13.7.2026.
  const cilTydne = (u) => Math.min(7, Math.max(1, parseInt(u.cil_tydne, 10) || 1));
  // mapa: odkud se cte plneni. Vychozi je 14denni okno, historie po mesicich
  // posila svoji mapu za vybrany mesic (9.9.2026).
  const weekCount = (u, dIso, mapa = plneni) => {
    const monday = weekMondayIso(dIso);
    let cnt = 0;
    for (let j = 0; j < 7; j++) {
      if (mapa[addDaysIsoLocal(monday, j)]?.[u.id] === true) cnt++;
    }
    return cnt;
  };
  const isDoneForDay = (u, dIso, mapa = plneni) => {
    if ((u.typ || "denni") !== "tydenni") return mapa[dIso]?.[u.id] === true;
    return weekCount(u, dIso, mapa) >= cilTydne(u);
  };

  // 7.8.2026: "nesplneno" se uz neuklada jako hodnota, odvozuje se. Klientka
  // nic "nehlasi" — staci, ze nezaskrtne a den se zavre. Den je uzavreny podle
  // stejneho pravidla jako zapis (mirror masterplanLib.canWriteDate): dnesek
  // je otevreny vzdy, vcerejsek cely nasledujici den, starsi dny uz nikdy.
  const vceraIso = addDaysIsoLocal(todayIso, -1);
  // 25. 10. 2026: včerejšek jde doplnit celý následující den (dřív jen do 10:00).
  const jeUzavren = (dIso) => {
    if (dIso >= todayIso) return false;
    if (dIso === vceraIso) return false;
    return true;
  };
  // Dny pred vznikem ukolu nejsou "nesplnene" — ukol tehdy jeste neexistoval.
  const platiOd = (u) => String(u.created_at || "").substring(0, 10);
  const nedeleIso = (dIso) => addDaysIsoLocal(weekMondayIso(dIso), 6);

  const cellFor = (u, dIso) => {
    if (isVolnoDay(dIso)) return <span className="text-sky-400" title="Volno od plánu">🌴</span>;
    if ((u.typ || "denni") === "tydenni") {
      const cil = cilTydne(u);
      const cnt = weekCount(u, dIso);
      const denZaskrtnut = plneni[dIso]?.[u.id] === true;
      // Cíl >1: ✓ jen ve dnech, kdy klient reálně odškrtl (progres x/cíl v titlu).
      const tydenZavren = jeUzavren(nedeleIso(dIso)) && dIso >= platiOd(u);
      if (cil > 1) {
        if (denZaskrtnut) return <span className={cnt >= cil ? "text-emerald-600 font-bold" : "text-sky-600 font-bold"} title={`Odškrtnuto tento den, týden ${cnt}/${cil}${cnt >= cil ? " ✓ splněno" : ""}`}>✓</span>;
        if (cnt < cil && tydenZavren) return <span className="text-rose-400" title={`Týden skončil na ${cnt}/${cil}, nesplněno`}>✗</span>;
        return <span className={cnt >= cil ? "text-emerald-300" : "text-slate-300"} title={`Týden ${cnt}/${cil}${cnt >= cil ? " ✓ splněno" : ""}`}>○</span>;
      }
      if (isDoneForDay(u, dIso)) return <span className="text-emerald-600 font-bold" title="Týdenní úkol: v tomto týdnu splněno">✓</span>;
      if (tydenZavren) return <span className="text-rose-400" title="Týdenní úkol: týden skončil nesplněný">✗</span>;
      return <span className="text-slate-300" title="Týdenní úkol, týden ještě běží">○</span>;
    }
    const v = plneni[dIso]?.[u.id];
    if (v === true) return <span className="text-emerald-600 font-bold">✓</span>;
    if (v === false || (jeUzavren(dIso) && dIso >= platiOd(u))) {
      return <span className="text-rose-400" title="Nezaškrtnuto, než se den uzavřel">✗</span>;
    }
    return <span className="text-slate-300" title={dIso >= platiOd(u) ? "Den je ještě otevřený" : "Úkol tehdy ještě neexistoval"}>○</span>;
  };

  const dayPct = (dIso) => {
    if (aktivni.length === 0 || isVolnoDay(dIso)) return null;
    const done = aktivni.filter((u) => isDoneForDay(u, dIso)).length;
    const any = aktivni.some((u) => plneni[dIso]?.[u.id] !== undefined) || done > 0;
    if (!any) return null;
    return Math.round((100 * done) / aktivni.length);
  };

  // 9.9.2026 — kolik procent dnu ma klientka u tohoto ukolu splneno. Berou se
  // jen dny, kdy uz ukol existoval, nejsou volno a den je uzavreny (nebo splneny).
  const ukolPct = (u) => {
    let celkem = 0;
    let hotovo = 0;
    for (const d of zobrazeneDny) {
      if (isVolnoDay(d.iso)) continue;
      if (d.iso < platiOd(u)) continue;
      const done = isDoneForDay(u, d.iso);
      if (!done && !jeUzavren(d.iso)) continue;
      celkem++;
      if (done) hotovo++;
    }
    if (!celkem) return null;
    return Math.round((100 * hotovo) / celkem);
  };

  // 9.9.2026 — jeden den v mesicnim kalendari ukolu.
  const stavDne = (u, dIso, mapa) => {
    if (isVolnoDay(dIso)) return "volno";
    if (dIso < platiOd(u) || dIso > todayIso) return "mimo";
    if (isDoneForDay(u, dIso, mapa)) return "splneno";
    if (jeUzavren(dIso)) return "nesplneno";
    return "otevreno";
  };
  // Procento a nejdelsi serie za cely mesic. Pocitaji se jen dny, ktere uz jsou
  // rozhodnute (splneno / nesplneno) — volno, budoucnost a otevrene dny se preskoci.
  const mesicStat = (u, seznamDnu, mapa) => {
    let celkem = 0, hotovo = 0, serie = 0, nejdelsi = 0;
    for (const d of seznamDnu) {
      const st = stavDne(u, d.iso, mapa);
      if (st === "splneno") { celkem++; hotovo++; serie++; if (serie > nejdelsi) nejdelsi = serie; }
      else if (st === "nesplneno") { celkem++; serie = 0; }
    }
    if (!celkem) return null;
    return { celkem, hotovo, serie: nejdelsi, pct: Math.round((100 * hotovo) / celkem) };
  };

  const aktualniMesic = todayIso.slice(0, 7);

  const hodnoceniFor = (dIso) => (data?.dny || []).find((d) => d.datum === dIso)?.hodnoceni || "";

  return (
    <div id="mp-sekce" className="space-y-4" style={{ scrollMarginTop: 90 }}>
      {err ? <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">{err}</div> : null}

      {/* Bez nároku: masterplán je jen pro Koučink FLEXIBLE / GOLD / PREMIUM.
          Historický masterplán (klient dřív balíček měl) zůstává viditelný jako
          archiv jen ke čtení: úkoly + plnění vidíte, ale nic nejde měnit a
          klientovi se v portálu nic nezobrazuje. */}
      {!eligible && (
        <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
          Masterplán je dostupný jen pro balíčky <strong>Koučink FLEXIBLE, GOLD a PREMIUM</strong>.
          {data?.balicek ? <> Klient má: <strong>{data.balicek}</strong>.</> : null}
          {aktivni.length > 0 ? " Historický masterplán níže zůstává jako archiv jen ke čtení, klientovi se nezobrazuje." : ""}
        </div>
      )}

      {/* Volno od plánu */}
      {(volno.active || volno.future) && (
        <div className="flex items-center gap-2 text-sm text-sky-700 bg-sky-50 border border-sky-200 rounded-xl px-3 py-2">
          <Palmtree className="w-4 h-4 flex-shrink-0" />
          {volno.active
            ? <span>Klientka má volno od plánu od {czDate(volno.active.od)} do {czDate(volno.active.do)}. Plnění se v tomto období nepočítá.</span>
            : <span>Klientka má naplánované volno od {czDate(volno.future.od)} do {czDate(volno.future.do)}.</span>}
        </div>
      )}

      {/* Žádosti klienta o změnu plánu */}
      {zadosti.length > 0 && (
        <div id="mp-zadosti" style={{ scrollMarginTop: 90 }}>
          <div className="flex items-center gap-2 mb-2">
            <MessageCircleQuestion className="w-4 h-4 text-rose-500" />
            <span className="text-sm font-semibold text-slate-700">Žádosti o změnu plánu</span>
          </div>
          <ul className="space-y-1.5">
            {zadosti.map((z) => (
              <li key={z.id} className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
                <span className="flex-1 text-sm text-slate-700">{z.text}</span>
                {canManage && (
                  <button onClick={() => resolveZadost(z.id)}
                    className="text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg px-2 py-1 whitespace-nowrap"
                    title="Označit jako vyřízené">
                    Vyřízeno
                  </button>
                )}
              </li>
            ))}
          </ul>
          <div className="text-[11px] text-slate-400 mt-1">Po úpravě úkolů označte žádost jako vyřízenou, klientce zmizí blokace další žádosti.</div>
        </div>
      )}

      {/* Aktivní úkoly */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <ClipboardList className="w-4 h-4 text-plum-600" />
          <span className="text-sm font-semibold text-slate-700">{eligible ? `Denní úkoly (${aktivni.length}/5)` : `Denní úkoly: archiv 🔒 (${aktivni.length})`}</span>
        </div>
        {aktivni.length === 0 ? (
          <div className="text-sm text-slate-400 italic">{eligible ? "Klient zatím nemá masterplán. Přidejte 3 až 5 denních úkolů." : "Klient nemá na masterplán nárok."}</div>
        ) : (
          <ul className="space-y-1.5">
            {aktivni.map((u) => (
              <li key={u.id} className={`flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 ${eligible ? "" : "opacity-60"}`}>
                {editingId === u.id ? (
                  <>
                    <input value={editEmoji} onChange={(e) => setEditEmoji(e.target.value)} maxLength={4} placeholder="🎯"
                      className="field w-12 text-center" />
                    <input value={editText} onChange={(e) => setEditText(e.target.value)} autoFocus
                      className="field min-w-[140px] flex-1" />
                    <input value={editKpi} onChange={(e) => setEditKpi(e.target.value)} placeholder="KPI (10000 kroků)"
                      className="field w-full sm:w-36" />
                    <span className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-[11px] font-bold whitespace-nowrap" title="Denní úkol se odškrtává každý den, týdenní stačí splnit jednou během týdne (Po až Ne)">
                      <button type="button" onClick={() => setEditTyp("denni")}
                        className={`px-2 py-1 ${editTyp !== "tydenni" ? "bg-plum-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>Denně</button>
                      <button type="button" onClick={() => setEditTyp("tydenni")}
                        className={`px-2 py-1 border-l border-slate-300 ${editTyp === "tydenni" ? "bg-sky-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>Týden</button>
                    </span>
                    {editTyp === "tydenni" && (
                      <label className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700 whitespace-nowrap" title="Kolikrát za týden má klient úkol splnit (např. trénink 3× týdně). Odškrtává po dnech.">
                        <input type="number" min={1} max={7} value={editCil}
                          onChange={(e) => setEditCil(Math.min(7, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                          className="field w-12 text-center" />
                        ×/týden
                      </label>
                    )}
                    <button onClick={saveEdit} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded min-h-[44px] sm:min-h-0" title="Uložit"><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditingId(null)} className="p-1 text-slate-400 hover:bg-slate-100 rounded" title="Zrušit"><X className="w-4 h-4" /></button>
                  </>
                ) : (
                  <>
                    {u.emoji ? <span className="text-base leading-none">{u.emoji}</span> : null}
                    <span className="min-w-0 basis-[calc(100%-2.5rem)] text-[15px] font-semibold leading-snug text-slate-800 sm:basis-auto sm:flex-1 sm:text-sm sm:font-normal sm:text-slate-700">{u.text}</span>
                    {uzka && ukolPct(u) != null && (
                      <span className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-[11.5px] font-bold ${ukolPct(u) >= 80 ? "bg-emerald-50 text-emerald-700" : ukolPct(u) >= 50 ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`} title="Splněno za zobrazené období">
                        {ukolPct(u)} %
                      </span>
                    )}
                    <span
                      className={`text-[11px] font-bold rounded-full px-2 py-0.5 whitespace-nowrap border ${u.typ === "tydenni" ? "text-sky-700 bg-sky-50 border-sky-200" : "text-plum-600 bg-plum-50 border-plum-200"}`}
                      title={u.typ === "tydenni" ? (cilTydne(u) > 1 ? `Týdenní úkol s cílem ${cilTydne(u)}× za týden, klient odškrtává po dnech` : "Týdenní úkol: stačí splnit jednou během týdne (Po až Ne)") : "Denní úkol: odškrtává se každý den znovu"}
                    >
                      {u.typ === "tydenni" ? (cilTydne(u) > 1 ? `${cilTydne(u)}× TÝDNĚ` : "TÝDEN") : "DENNĚ"}
                    </span>
                    {u.kpi ? <span className="rounded-full border border-plum-200 bg-plum-50 px-2 py-0.5 text-xs font-medium text-plum-700 whitespace-nowrap">{u.kpi}</span> : null}
                    {canManage && eligible && (
                      // 9.9.2026 — na telefonu jsou akce tri tlacitka pres celou sirku pod pilulkami,
                      // kazde vysoke 44 px. Na pocitaci zustavaji male ikony v radku.
                      <div className={uzka ? "mt-1 flex w-full basis-full items-center gap-2" : "contents"}>
                        <button
                          onClick={() => { setEditingId(u.id); setEditText(u.text); setEditEmoji(u.emoji || ""); setEditKpi(u.kpi || ""); setEditTyp(u.typ || "denni"); setEditCil(cilTydne(u)); }}
                          className={uzka ? "flex h-11 flex-1 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600" : "rounded p-1 text-slate-400 hover:bg-plum-50 hover:text-plum-600"}
                          title="Upravit"
                        >
                          <Pencil className={uzka ? "h-4 w-4" : "h-3.5 w-3.5"} />
                        </button>
                        <button
                          onClick={() => archive(u)}
                          className={uzka ? "flex h-11 flex-1 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600" : "rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"}
                          title="Archivovat (klientovi zmizí, historie zůstane)"
                        >
                          <Archive className={uzka ? "h-4 w-4" : "h-3.5 w-3.5"} />
                        </button>
                        {isOwner && (
                          <button
                            onClick={() => hardDelete(u)}
                            className={uzka ? "flex h-11 flex-1 items-center justify-center rounded-xl border border-rose-200 bg-white text-rose-600" : "rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-700"}
                            title="Smazat úkol i s historií plnění (jen owner, nejde vrátit)"
                          >
                            <Trash2 className={uzka ? "h-4 w-4" : "h-3.5 w-3.5"} />
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        {(canManage || canSuggest) && eligible && uzka && !pridavaniOtevreno && (
          <button
            type="button"
            onClick={() => setPridavaniOtevreno(true)}
            className="mt-2 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 text-sm font-bold text-slate-600"
          >
            <Plus className="h-4 w-4" /> {canManage ? "Přidat úkol" : "Navrhnout úkol"}
          </button>
        )}
        {(canManage || canSuggest) && eligible && (!uzka || pridavaniOtevreno) && (
          <div className="mt-2 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={newEmoji}
                onChange={(e) => setNewEmoji(e.target.value)}
                maxLength={4}
                placeholder="🎯"
                className="field w-12 text-center"
                title="Emoji úkolu (nepovinné)"
              />
              <input
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addTask(); }}
                placeholder={canManage ? "Nový denní úkol, např. Dnes jsem ušla alespoň 10 000 kroků" : "Návrh úkolu pro management ke schválení"}
                className="field min-w-[150px] flex-1"
              />
              <input
                value={newKpi}
                onChange={(e) => setNewKpi(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addTask(); }}
                placeholder="KPI (10000 kroků)"
                className="field w-full sm:w-40"
                title="Měřitelný cíl úkolu (nepovinné)"
              />
              <span className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-[11px] font-bold whitespace-nowrap self-center" title="Denní úkol se odškrtává každý den, týdenní stačí splnit jednou během týdne (Po až Ne)">
                <button type="button" onClick={() => setNewTyp("denni")}
                  className={`px-2.5 py-1.5 ${newTyp !== "tydenni" ? "bg-plum-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>Denně</button>
                <button type="button" onClick={() => setNewTyp("tydenni")}
                  className={`px-2.5 py-1.5 border-l border-slate-300 ${newTyp === "tydenni" ? "bg-sky-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>Týden</button>
                {newTyp === "tydenni" && (
                  <label className="inline-flex items-center gap-1 px-2 border-l border-slate-300 text-[11px] font-semibold text-sky-700 whitespace-nowrap bg-sky-50" title="Kolikrát za týden má klient úkol splnit (např. trénink 3× týdně). Odškrtává po dnech.">
                    <input type="number" min={1} max={7} value={newCil}
                      onChange={(e) => setNewCil(Math.min(7, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                      className="field w-10 text-center" />
                    ×/týden
                  </label>
                )}
              </span>
              <button
                onClick={addTask}
                disabled={adding || !newText.trim()}
                className="btn btn-primary"
              >
                <Plus className="w-4 h-4" /> {canManage ? "Přidat" : "Navrhnout"}
              </button>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-[11px] text-slate-400 mr-1">Emoji:</span>
              {EMOJI_PRESETS.map((e) => (
                <button key={e} onClick={() => setNewEmoji(e)}
                  className={`text-sm px-1.5 py-0.5 rounded hover:bg-plum-50 ${newEmoji === e ? "bg-plum-100" : ""}`}>{e}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Návrhy od trenérů */}
      {navrhy.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-semibold text-slate-700">Návrhy ke schválení</span>
          </div>
          <ul className="space-y-1.5">
            {navrhy.map((u) => (
              <li key={u.id} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                {u.emoji ? <span className="text-base leading-none">{u.emoji}</span> : null}
                <span className="flex-1 text-sm text-slate-700">{u.text}</span>
                {u.kpi ? <span className="text-xs font-medium text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5 whitespace-nowrap">{u.kpi}</span> : null}
                <span className="text-xs text-amber-600 whitespace-nowrap">navrhl: {u.navrh_od || "?"}</span>
                {canManage && eligible && (
                  <>
                    <button onClick={() => approve(u.id)} className="p-1 text-emerald-600 hover:bg-emerald-100 rounded" title="Schválit"><Check className="w-4 h-4" /></button>
                    <button onClick={() => archive(u)} className="p-1 text-rose-500 hover:bg-rose-100 rounded" title="Zamítnout"><X className="w-4 h-4" /></button>
                  </>
                )}
                {canSuggest && (u.navrh_od === (user?.name || user?.email)) && (
                  <button onClick={() => archive(u)} className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded" title="Zahodit návrh"><X className="w-4 h-4" /></button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Plnění za posledních 14 dní */}
      {aktivni.length > 0 && (
        <div>
          <div className={`${uzka ? "hidden" : ""} mb-2 text-sm font-semibold text-slate-700`}>Plnění za posledních 14 dní</div>
          <div className={`${uzka ? "hidden" : ""} overflow-x-auto rounded-lg border border-slate-200`}>
            <table className="text-xs border-collapse w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left text-slate-500 font-medium pl-3 pr-3 py-1.5 whitespace-nowrap">Úkol</th>
                  {zobrazeneDny.map((d) => (
                    <th key={d.iso} className={`px-1.5 py-1.5 font-medium whitespace-nowrap ${d.iso === todayIso ? "text-plum-600 bg-plum-50" : "text-slate-400"}`}>{d.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {aktivni.map((u, i) => (
                  <tr key={u.id} className={`border-t border-slate-100 ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}>
                    <td className="pl-3 pr-3 py-1.5 text-slate-700 min-w-[220px] max-w-[340px]">
                      <span className="align-middle whitespace-normal leading-snug">{u.emoji ? u.emoji + " " : ""}{u.text}{u.kpi ? <span className="text-slate-400">, {u.kpi}</span> : null}</span>
                      {(u.typ || "denni") === "tydenni" && (
                        <span className="ml-1.5 align-middle inline-block text-[11px] font-bold tracking-wide text-sky-700 bg-sky-100 border border-sky-200 rounded px-1 py-px whitespace-nowrap" title={cilTydne(u) > 1 ? `Týdenní úkol s cílem ${cilTydne(u)}× za týden` : "Týdenní úkol: stačí splnit kdykoli během týdne, počítá se pro celý týden"}>{cilTydne(u) > 1 ? `${cilTydne(u)}× TÝDNĚ` : "TÝDEN"}</span>
                      )}
                    </td>
                    {zobrazeneDny.map((d) => (
                      <td key={d.iso} className={`px-1.5 py-1.5 text-center ${d.iso === todayIso ? "bg-plum-50/70" : ""}`}>{cellFor(u, d.iso)}</td>
                    ))}
                  </tr>
                ))}
                <tr className="border-t-2 border-slate-200 bg-slate-50">
                  <td className="pl-3 pr-3 py-1.5 text-slate-600 font-semibold">Splněno</td>
                  {zobrazeneDny.map((d) => {
                    const pct = dayPct(d.iso);
                    const hod = hodnoceniFor(d.iso);
                    return (
                      <td key={d.iso} title={hod || undefined} className={`px-1.5 py-1.5 text-center font-semibold ${d.iso === todayIso ? "bg-plum-50/70" : ""} ${pct == null ? "text-slate-300" : pct >= 80 ? "text-emerald-600" : pct >= 50 ? "text-amber-600" : "text-rose-500"}`}>
                        {pct == null ? "—" : `${pct}%`}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
          {/* Vysvětlivky přehledně jako legenda místo jedné dlouhé věty.
              9.9.2026: na telefonu sbalené pod tlačítko, ať nezabírají půl obrazovky. */}
          <div className={`${uzka ? "hidden" : "flex"} flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 mt-2`}>
            <span className="flex items-center gap-1"><span className="text-emerald-600 font-bold">✓</span> splněno</span>
            <span className="flex items-center gap-1"><span className="text-rose-400">✗</span> nesplněno (nezaškrtnuto, než se den uzavřel)</span>
            <span className="flex items-center gap-1"><span className="text-slate-300 font-bold">○</span> den ještě otevřený nebo úkol tehdy neexistoval</span>
            <span className="flex items-center gap-1"><span className="text-sky-400">🌴</span> volno od plánu</span>
            <span className="flex items-center gap-1"><span className="text-[11px] font-bold tracking-wide text-sky-700 bg-sky-100 border border-sky-200 rounded px-1 py-px">TÝDEN</span> stačí splnit kdykoli v týdnu (Po až Ne), počítá se pro celý týden</span>
          </div>
          <div className={`${uzka ? "hidden" : ""} mt-1 text-[11px] text-slate-400`}>Najetím myší na procento v řádku „Splněno" uvidíte automatické hodnocení dne.</div>

          {/* 9.9.2026 — historie kazdeho ukolu po mesicich. Nahrazuje sedmidenni pas
              na telefonu a na pocitaci doplnuje tabulku o starsi mesice. Data se
              stahuji az pri rozbaleni, at se stranka nacita stejne rychle jako dnes. */}
          <div className="mt-4 space-y-2">
            <div className="text-sm font-semibold text-slate-700">Historie úkolů po měsících</div>
            {aktivni.map((u) => {
              const otevreno = otevrenyUkol === u.id;
              const pct14 = ukolPct(u);
              const mapa = mesicCache[mesic];
              const mrizka = otevreno ? dnyMesice(mesic) : null;
              const stat = otevreno && mapa ? mesicStat(u, mrizka.dny, mapa) : null;
              return (
                <div key={u.id} className={`rounded-xl border bg-white ${otevreno ? "border-plum-300" : "border-slate-200"}`}>
                  <button
                    type="button"
                    onClick={() => setOtevrenyUkol(otevreno ? null : u.id)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      {u.emoji ? <span className="text-base leading-none">{u.emoji}</span> : null}
                      <span className="truncate text-[13.5px] font-semibold text-slate-800">{u.text}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                      {u.kpi ? <span className="hidden rounded-full border border-plum-200 bg-plum-50 px-2 py-0.5 text-[11px] font-medium text-plum-700 sm:inline">{u.kpi}</span> : null}
                      {pct14 != null && (
                        <span className={`rounded-full px-2 py-0.5 text-[11.5px] font-bold ${pct14 >= 80 ? "bg-emerald-50 text-emerald-700" : pct14 >= 50 ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`} title="Splněno za posledních 14 dní">
                          {pct14} %
                        </span>
                      )}
                      {otevreno ? <ChevronUp className="h-4 w-4 text-plum-600" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                    </span>
                  </button>
                  {otevreno && (
                    <div className="border-t border-slate-100 p-3">
                      {/* 9.9.2026 — přepínač měsíce je pruh přes celou šířku karty,
                          aktivní šipka je bílá dlaždice. Odznaky pod ním, každý přes půl šířky. */}
                      <div className="flex items-center gap-0.5 rounded-xl bg-plum-50 p-1">
                        <button type="button" onClick={() => setMesic(posunMesic(mesic, -1))}
                          className="flex h-9 w-10 flex-none items-center justify-center rounded-xl bg-white text-plum-600 shadow-sm" title="Předchozí měsíc">
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <span className="flex-1 text-center text-[14.5px] font-extrabold text-plum-800">{mesicLabel(mesic)}</span>
                        <button type="button" disabled={mesic >= aktualniMesic} onClick={() => setMesic(posunMesic(mesic, 1))}
                          className={`flex h-9 w-10 flex-none items-center justify-center rounded-xl ${mesic >= aktualniMesic ? "text-plum-300" : "bg-white text-plum-600 shadow-sm"}`} title="Další měsíc">
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                      {stat ? (
                        <div className="mt-2 flex items-center gap-1.5">
                          {stat.serie > 1 ? <span className="flex-1 rounded-full bg-plum-50 px-2 py-1 text-center text-[11.5px] font-bold text-plum-700" title="Nejdelší série splněných dnů v tomto měsíci">série {stat.serie} dní</span> : null}
                          <span className={`flex-1 rounded-full px-2 py-1 text-center text-[11.5px] font-bold ${stat.pct >= 80 ? "bg-emerald-50 text-emerald-700" : stat.pct >= 50 ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>
                            {stat.pct} % <span className="opacity-40">|</span> {stat.hotovo} z {stat.celkem}
                          </span>
                        </div>
                      ) : null}
                      {!mapa ? (
                        <div className="py-4 text-center text-sm text-slate-400">{mesicNacita ? "Načítám měsíc…" : "Data se nepodařilo načíst"}</div>
                      ) : (
                        <>
                          <div className="mt-2.5 grid grid-cols-7 gap-1">
                            {DNY_ZKRATKY.map((z) => (
                              <div key={z} className="pb-0.5 text-center text-[11px] font-bold text-slate-400">{z}</div>
                            ))}
                            {Array.from({ length: mrizka.offset }).map((_, i) => <div key={`prazdno-${i}`} />)}
                            {mrizka.dny.map((d) => {
                              const st = stavDne(u, d.iso, mapa);
                              return (
                                <div key={d.iso}
                                  title={`${czDate(d.iso)}: ${POPIS_DNE[st]}`}
                                  className={`flex h-8 items-center justify-center rounded-xl text-[11.5px] font-bold ${TRIDA_DNE[st]} ${d.iso === todayIso ? "ring-2 ring-plum-400" : ""}`}>
                                  {d.den}
                                </div>
                              );
                            })}
                          </div>
                          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-plum-600" /> splněno</span>
                            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-rose-100" /> nesplněno</span>
                            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-sky-50 border border-sky-200" /> volno</span>
                            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-slate-50 border border-slate-200" /> mimo plán</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Poznámky klienta z portálu — 10.7.: seskupené po měsících jako
          rozbalovací sekce (nejnovější měsíc otevřený), ať se sekce
          nenafukuje, až poznámek budou desítky. */}
      {dnyKlient.length > 0 && (
        <div id="mp-komentare" style={{ scrollMarginTop: 90 }}>
          <div className="text-sm font-semibold text-slate-700 mb-2">
            Poznámky klienta
            <span className="ml-2 text-xs font-normal text-slate-400">{dnyKlient.length} celkem</span>
          </div>
          <div className="space-y-1.5">
            {noteMonths.map(({ key, label, notes }, idx) => {
              const open = openMonths[key] ?? idx === 0; // nejnovější měsíc default otevřený
              return (
                <div key={key} className="border border-slate-200 rounded-xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenMonths((o) => ({ ...o, [key]: !open }))}
                    className="w-full flex items-center justify-between gap-2 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-left"
                  >
                    <span className="text-xs font-semibold text-slate-600 capitalize">{label}</span>
                    <span className="flex items-center gap-2 text-xs text-slate-400">
                      {notes.length} {notes.length === 1 ? "poznámka" : notes.length < 5 ? "poznámky" : "poznámek"}
                      <span className={`transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
                    </span>
                  </button>
                  {open && (
                    <ul className="divide-y divide-slate-100 bg-white">
                      {notes.map((d) => (
                        <li key={d.datum} className="flex flex-col items-start gap-1 px-3 py-2.5 sm:flex-row sm:items-start sm:gap-2.5 sm:py-2">
                          <span className="flex-shrink-0 text-[11px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-lg px-1.5 py-0.5 mt-px whitespace-nowrap">
                            {czDate(d.datum)}
                          </span>
                          <span className="w-full text-sm leading-relaxed text-slate-700">{d.poznamka_klient}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Poznámka kouče k dnešku */}
      {(canManage || canSuggest) && eligible && aktivni.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-1.5">
            Poznámky kouče
            {koucNotesAll.length > 0 && <span className="ml-2 text-xs font-normal text-slate-400">{koucNotesAll.length} celkem</span>}
          </div>
          <div className="flex items-start gap-2">
            <RostouciPole
              value={koucNote}
              onChange={(e) => { setKoucNote(e.target.value); setNoteSaved(false); }}
              minRows={4}
              placeholder="Napsat novou poznámku pro klientku…"
              className="field flex-1 leading-relaxed"
            />
            <button
              onClick={saveKoucNote}
              disabled={savingNote || !koucNote.trim()}
              className={`text-sm font-medium text-white disabled:opacity-50 rounded-lg px-3 py-1.5 transition-colors ${noteSaved ? "bg-emerald-600" : "bg-slate-700 hover:bg-slate-800"}`}
            >
              {savingNote ? "Ukládám…" : noteSaved ? "Uloženo ✓" : "Přidat"}
            </button>
          </div>
          <div className="text-[11px] text-slate-400 mt-1">Poznámka se přidá jako komentář (historie zůstává). Klientka v portálu uvidí nejnovější jako „💬 Vzkaz od kouče".</div>
          {/* Historie poznámek kouče — po měsících jako u poznámek klienta */}
          {koucMonths.length > 0 && (
            <div className="space-y-1.5 mt-2">
              {koucMonths.map(({ key, label, notes }, idx) => {
                const oKey = "kouc:" + key;
                const open = openMonths[oKey] ?? idx === 0;
                return (
                  <div key={oKey} className="border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenMonths((o) => ({ ...o, [oKey]: !open }))}
                      className="w-full flex items-center justify-between gap-2 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-left"
                    >
                      <span className="text-xs font-semibold text-slate-600 capitalize">{label}</span>
                      <span className="flex items-center gap-2 text-xs text-slate-400">
                        {notes.length} {notes.length === 1 ? "poznámka" : notes.length < 5 ? "poznámky" : "poznámek"}
                        <span className={`transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
                      </span>
                    </button>
                    {open && (
                      <ul className="divide-y divide-slate-100 bg-white">
                        {notes.map((d) => (
                          <li key={d.datum} className="group flex flex-col items-start gap-1 px-3 py-2.5 sm:flex-row sm:items-start sm:gap-2.5 sm:py-2">
                            <span className="flex-shrink-0 text-[11px] font-semibold text-plum-600 bg-plum-50 border border-plum-100 rounded-lg px-1.5 py-0.5 mt-px whitespace-nowrap">
                              {czDate(d.datum)}
                            </span>
                            <span className="w-full flex-1 whitespace-pre-line text-sm leading-relaxed text-slate-700">{d.poznamka_kouc}</span>
                            <button
                              type="button"
                              onClick={() => deleteKoucNote(d)}
                              title="Smazat poznámku"
                              className="flex-shrink-0 p-1 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Designový confirm pro archivaci a mazání (místo window.confirm) */}
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title || ""}
        description={confirmState?.description}
        confirmLabel={confirmState?.confirmLabel || "Potvrdit"}
        variant={confirmState?.variant || "danger"}
        onConfirm={() => { const fn = confirmState?.onConfirm; setConfirmState(null); if (fn) fn(); }}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
}
